import { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertOctagon,
  AlertTriangle,
  CircleDot,
  XCircle,
  Cross,
  Ban,
  Shield,
} from "lucide-react";

// Status visual config — Titans palette only
export const INJURY_STATUS_META = {
  Out:          { Icon: XCircle,       color: "#C8102E", bg: "bg-titans-red",   ring: "border-titans-red",       label: "Out" },
  Doubtful:     { Icon: AlertOctagon,  color: "#E67E22", bg: "bg-orange-600",   ring: "border-orange-500",       label: "Doubtful" },
  Questionable: { Icon: AlertTriangle, color: "#F5C518", bg: "bg-yellow-500",   ring: "border-yellow-500",       label: "Questionable" },
  Probable:     { Icon: CircleDot,     color: "#4B92DB", bg: "bg-titans-blue",  ring: "border-titans-blue",      label: "Probable" },
  IR:           { Icon: Cross,         color: "#8a9099", bg: "bg-titans-elev",  ring: "border-titans-silver/40", label: "Injured Reserve" },
  PUP:          { Icon: Shield,        color: "#8a9099", bg: "bg-titans-elev",  ring: "border-titans-silver/40", label: "PUP List" },
  Suspended:    { Icon: Ban,           color: "#8a9099", bg: "bg-titans-navy",  ring: "border-titans-silver/40", label: "Suspended" },
};

const STATUS_ORDER = ["Out", "Doubtful", "Questionable", "Probable", "IR", "PUP", "Suspended"];

export default function InjuryReport() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/injuries");
      setItems(data.injuries || []);
    } catch (e) {
      console.error("InjuryReport load failed:", e);
      toast.error("Injury report failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let r = [...items];
    r.sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
    if (filter !== "All") r = r.filter((i) => i.status === filter);
    return r;
  }, [items, filter]);

  const counts = useMemo(() => {
    const c = { Out: 0, Doubtful: 0, Questionable: 0, Probable: 0, IR: 0, PUP: 0, Suspended: 0 };
    items.forEach((i) => { if (c[i.status] !== undefined) c[i.status] += 1; });
    return c;
  }, [items]);

  const lastUpdated = useMemo(() => {
    const t = items
      .map((i) => i.updated_at)
      .filter(Boolean)
      .sort()
      .pop();
    return t || null;
  }, [items]);

  return (
    <div data-testid="injury-report-page">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue">Practice + Game Status</div>
          <h2 className="font-display text-3xl md:text-4xl uppercase tracking-wide section-title">Injury Report</h2>
        </div>
        {lastUpdated && (
          <div className="text-titans-silver text-xs font-cond uppercase tracking-wider">
            Updated {formatDate(lastUpdated, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </div>
        )}
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="injury-filters">
        {["All", ...STATUS_ORDER].map((s) => {
          const meta = INJURY_STATUS_META[s];
          const active = filter === s;
          return (
            <button
              key={s}
              data-testid={`injury-filter-${s.toLowerCase()}`}
              onClick={() => setFilter(s)}
              className={`font-cond uppercase tracking-wider text-xs px-4 py-2 border inline-flex items-center gap-2 transition-colors ${
                active
                  ? "bg-titans-red border-titans-red text-white"
                  : "border-[rgba(138,141,143,0.3)] text-titans-silver hover:text-white hover:border-white"
              }`}
            >
              {meta?.Icon && <meta.Icon size={12} />}
              {s}
              {s !== "All" && (
                <span className="text-[10px] text-titans-silver/80 ml-0.5">
                  {counts[s] || 0}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="injury-legend">
        {STATUS_ORDER.slice(0, 4).map((s) => {
          const m = INJURY_STATUS_META[s];
          return (
            <div key={s} className="titans-card p-3 flex items-center gap-3" style={{ borderLeft: `4px solid ${m.color}` }}>
              <m.Icon size={18} style={{ color: m.color }} />
              <div>
                <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">{m.label}</div>
                <div className="font-display text-xl">{counts[s] || 0}</div>
              </div>
            </div>
          );
        })}
      </div>

      {loading ? (
        <div className="text-titans-silver">Loading injury report…</div>
      ) : filtered.length === 0 ? (
        <div className="titans-card p-10 text-center">
          <Shield size={48} className="mx-auto text-titans-blue mb-3" />
          <div className="font-display text-2xl uppercase">All Clear</div>
          <div className="text-titans-silver font-body mt-1">
            {filter === "All"
              ? "No injuries currently logged for the Titans."
              : `No players currently listed as ${filter}.`}
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="injury-list">
          {filtered.map((it, idx) => {
            const meta = INJURY_STATUS_META[it.status] || INJURY_STATUS_META.Questionable;
            return (
              <div
                key={it.player_id}
                data-testid={`injury-row-${idx}`}
                className="titans-card hover-rise grid grid-cols-12 items-center gap-4 p-4"
                style={{ borderLeft: `5px solid ${meta.color}` }}
              >
                <div className="col-span-12 md:col-span-1 flex md:justify-center">
                  {it.headshot ? (
                    <img src={it.headshot} alt={it.full_name} className="h-14 w-14 object-cover bg-titans-elev" />
                  ) : (
                    <div className="h-14 w-14 bg-titans-elev flex items-center justify-center font-display text-lg text-titans-silver">
                      {it.jersey ? `#${it.jersey}` : (it.position || "—")}
                    </div>
                  )}
                </div>
                <div className="col-span-8 md:col-span-4">
                  <div className="font-display text-xl uppercase leading-tight">{it.full_name}</div>
                  <div className="font-cond uppercase tracking-wider text-[10px] text-titans-blue mt-1">
                    {it.position || "—"}{it.jersey ? ` · #${it.jersey}` : ""}
                  </div>
                </div>
                <div className="col-span-4 md:col-span-2">
                  <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Status</div>
                  <div className="inline-flex items-center gap-1.5 mt-1 font-cond uppercase tracking-wider text-xs px-2 py-1" style={{ background: meta.color, color: "#fff" }}>
                    <meta.Icon size={12} />
                    {it.status}
                  </div>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Injury</div>
                  <div className="font-body text-white text-sm mt-1">{it.injury || "—"}</div>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Note</div>
                  <div className="font-body text-titans-silver text-sm mt-1 leading-snug">
                    {it.note || (it.game_week ? `Week ${it.game_week} designation` : "—")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
